import type { Coordinate, MultiPolygonCoordinates } from '../models/editor-feature';

/**
 * Turning drawing units into metres, and back.
 *
 * Floorplan coordinates are arbitrary local units: every drawing arrives on its own scale,
 * and nothing in the geometry says which. A 17,126-unit-wide plan is as likely to be
 * millimetres as decimetres, so the conversion cannot be inferred — it has to be measured
 * once, against something whose real length is known.
 *
 * Everything that has to mean something physical depends on that one number: a measured
 * distance, an area, detecting desks by catalogue size, stamping a desk group defined in
 * metres, drawing a wall at a real thickness. Where it is missing, the honest thing is to say
 * so rather than to print a figure in units and let it be read as metres — which is why every
 * function here returns null instead of guessing.
 */

/** How many drawing units make one metre, or null when nobody has measured it. */
export type Scale = number | null;

export const isUsableScale = (scale: unknown): scale is number => (
  typeof scale === 'number' && Number.isFinite(scale) && scale > 0
);

export const distanceBetween = (from: Coordinate, to: Coordinate): number => (
  Math.hypot(to[0] - from[0], to[1] - from[1])
);

/**
 * The scale implied by dragging across something of known length.
 *
 * Null rather than throwing for a drag that went nowhere or a length of zero: both are
 * things an admin does by accident, and neither should end with a scale of infinity written
 * to the plan.
 */
export const scaleFromMeasurement = (
  from: Coordinate,
  to: Coordinate,
  realMetres: number,
): Scale => {
  const drawn = distanceBetween(from, to);

  if (!Number.isFinite(realMetres) || realMetres <= 0 || drawn <= 0) {
    return null;
  }

  return drawn / realMetres;
};

export const unitsToMetres = (units: number, scale: Scale): number | null => (
  isUsableScale(scale) ? units / scale : null
);

export const metresToUnits = (metres: number, scale: Scale): number | null => (
  isUsableScale(scale) ? metres * scale : null
);

/**
 * The signed area of a ring, by the shoelace formula.
 *
 * Signed on purpose: the sign is the winding direction, which is what tells an inner ring
 * (a hole) from an outer one in a well-formed polygon.
 */
export const ringArea = (ring: readonly Coordinate[]): number => {
  let total = 0;

  for (let index = 0; index < ring.length; index += 1) {
    const [x1, y1] = ring[index];
    const [x2, y2] = ring[(index + 1) % ring.length];

    total += x1 * y2 - x2 * y1;
  }

  return total / 2;
};

/**
 * The area a shape covers, in square drawing units.
 *
 * Rings after the first in a polygon are subtracted, so a room with a core in it reports the
 * floor somebody can actually stand on rather than the area of its outline.
 */
export const polygonArea = (coordinates: MultiPolygonCoordinates): number => coordinates.reduce(
  (total, polygon) => total + polygon.reduce(
    (area, ring, index) => area + (index === 0 ? Math.abs(ringArea(ring)) : -Math.abs(ringArea(ring))),
    0,
  ),
  0,
);

/** The perimeter of a run of points, which is what a measure-distance drag produces. */
export const pathLength = (path: readonly Coordinate[]): number => path.reduce(
  (total, point, index) => (index === 0 ? 0 : total + distanceBetween(path[index - 1], point)),
  0,
);

/**
 * A length as the admin should read it.
 *
 * Without a scale it is shown in units and labelled as units — never as a bare number, which
 * would be read as metres by anybody who did not know the plan had never been measured.
 */
export const formatLength = (units: number, scale: Scale): string => {
  const metres = unitsToMetres(units, scale);

  if (metres === null) {
    return `${units.toFixed(1)} units`;
  }

  // Centimetres below a metre: "0.4 m" reads as a rounding artefact where "42 cm" reads as a
  // measurement, and door and wall thicknesses live entirely in that range.
  return metres < 1 ? `${Math.round(metres * 100)} cm` : `${metres.toFixed(2)} m`;
};

export const formatArea = (squareUnits: number, scale: Scale): string => {
  if (!isUsableScale(scale)) {
    return `${squareUnits.toFixed(1)} units²`;
  }

  return `${(squareUnits / (scale * scale)).toFixed(2)} m²`;
};
